import { useFormik } from 'formik';
import React, { useState } from 'react';
import { Trans } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import * as yup from 'yup';

import { AuthShell } from '@/components/auth/AuthShell';
import { ContinueWithProviderButtons } from '@/components/common/ContinueWithProviderButtons';
import { Button, Field, TextInput } from '@/components/ds';
import { useAuthContext } from '@/context/AuthProvider';
import { requestSelfSignup } from '@/services/api/invites';
import { useTranslationPrefix } from '@/translations/i18n';

export interface RegisterLocationState {
  email?: string;
}

const legalLinkStyle: React.CSSProperties = { color: 'inherit', textDecoration: 'underline', textUnderlineOffset: 3 };

const Register: React.FC = () => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const initialEmail = (location.state as RegisterLocationState | undefined)?.email;
  const t = useTranslationPrefix('register');
  const validationT = useTranslationPrefix('validation');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const registerValidationSchema = yup.object({
    email: yup
      .string()
      .email(validationT('valid', { name: t('email') }))
      .required(validationT('required', { name: t('email') })),
  });

  const registerFormik = useFormik({
    initialValues: {
      email: initialEmail ?? '',
    },
    validationSchema: registerValidationSchema,
    validateOnBlur: false,
    onSubmit: async (values) => {
      try {
        setError(null);
        setIsSubmitting(true);
        await requestSelfSignup(values.email);
        navigate('/email-verification-sent', { state: { email: values.email } });
      } catch (error: any) {
        setError(error.message);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const emailError = registerFormik.touched.email && registerFormik.errors.email;

  return (
    <AuthShell
      title="Create account"
      footer={
        <>
          {t('already_have_account.0')}
          <Link className="hx-auth-link" to="/login" data-testid="register-login-link">
            {t('already_have_account.1')}
          </Link>
        </>
      }>
      <style>{`.hx-auth-field-error { font-size: 11.5px; font-weight: 500; color: #DB5282; }`}</style>
      <form onSubmit={registerFormik.handleSubmit} data-testid="register-form">
        <h1 className="hx-auth-title">Create your account</h1>
        <p className="hx-auth-sub">Start with your email. We&apos;ll send a link to finish setting up.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field label={t('email')} htmlFor="register-email">
            <TextInput
              id="register-email"
              name="email"
              type="email"
              required
              autoComplete="email"
              autoFocus
              placeholder="you@example.com"
              value={registerFormik.values.email}
              onChange={registerFormik.handleChange}
              onBlur={registerFormik.handleBlur}
              error={Boolean(emailError)}
              inputProps={{ 'aria-label': t('email'), 'data-testid': 'register-email-input' }}
            />
            {emailError ? <span className="hx-auth-field-error">{emailError}</span> : null}
          </Field>
          {error && (
            <p className="hx-auth-error" data-testid="register-error-message">
              {error}
            </p>
          )}
          <Button type="submit" variant="primary" block disabled={isSubmitting} data-testid="register-submit-btn">
            {isSubmitting ? 'Creating account…' : 'Create account'}
          </Button>
          {/* single sign-on under the form, a quiet tray button, so lime Create account stays the one primary action */}
          <ContinueWithProviderButtons onError={setError} />
          <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.55, color: 'rgba(247, 247, 246, 0.45)', textAlign: 'center' }}>
            <Trans
              i18nKey={t('accept_terms')}
              components={{
                TermsLink: (
                  <a
                    href="https://hauserhq.com/legal/terms-of-service"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={legalLinkStyle}
                  />
                ),
                PrivacyLink: (
                  <a
                    href="https://hauserhq.com/legal/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={legalLinkStyle}
                  />
                ),
              }}
            />
          </p>
        </div>
      </form>
    </AuthShell>
  );
};

export default Register;
